import React from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import MessageModal from '../components/MessageModal';
import useReportDetailController from '../features/report-detail/useReportDetailController';
import { markdownSanitizeOptions } from '../utils/markdownSanitizeOptions';
import tokens from '../styles/appWebviewTokens';

const { colors, spacing, radius } = tokens;

const AppMarkdown = ({ children }) => (
  <ReactMarkdown
    rehypePlugins={[rehypeRaw, [rehypeSanitize, markdownSanitizeOptions]]}
    components={{
      h3: ({ node, children, ...props }) => {
        void node;
        return (
          <h3
            style={{
              margin: '1.4em 0 0.7em',
              fontSize: 20,
              lineHeight: 1.35,
              fontWeight: 700,
              color: colors.textStrong,
              paddingBottom: 6,
              borderBottom: `2px solid ${colors.primary}`,
            }}
            {...props}
          >
            {children}
          </h3>
        );
      },
      h4: ({ node, children, ...props }) => {
        void node;
        return (
          <h4
            style={{
              margin: '1.1em 0 0.6em',
              fontSize: 16,
              lineHeight: 1.4,
              fontWeight: 700,
              color: colors.textStrong,
              paddingLeft: 10,
              borderLeft: `4px solid ${colors.primary}`,
            }}
            {...props}
          >
            {children}
          </h4>
        );
      },
      p: ({ node, ...props }) => {
        void node;
        return (
          <p
            style={{
              margin: '0 0 0.9em',
              fontSize: 15,
              lineHeight: 1.72,
              color: colors.text,
              wordBreak: 'normal',
              overflowWrap: 'break-word',
            }}
            {...props}
          />
        );
      },
      ul: ({ node, ...props }) => {
        void node;
        return (
          <ul
            style={{
              margin: '0.4em 0 0.9em',
              paddingLeft: 20,
              color: colors.text,
            }}
            {...props}
          />
        );
      },
      ol: ({ node, ...props }) => {
        void node;
        return (
          <ol
            style={{
              margin: '0.4em 0 0.9em',
              paddingLeft: 20,
              color: colors.text,
            }}
            {...props}
          />
        );
      },
      li: ({ node, ...props }) => {
        void node;
        return (
          <li
            style={{
              marginBottom: 8,
              fontSize: 15,
              lineHeight: 1.72,
              color: colors.text,
              wordBreak: 'normal',
              overflowWrap: 'break-word',
            }}
            {...props}
          />
        );
      },
      strong: ({ node, ...props }) => {
        void node;
        return (
          <strong
            style={{
              color: colors.primary,
              fontWeight: 700,
            }}
            {...props}
          />
        );
      },
      blockquote: ({ node, ...props }) => {
        void node;
        return (
          <blockquote
            style={{
              margin: '1em 0',
              padding: '10px 12px',
              borderLeft: `4px solid ${colors.primary}`,
              borderRadius: radius.md,
              background: colors.primarySoft,
              color: colors.text,
            }}
            {...props}
          />
        );
      },
    }}
  >
    {children}
  </ReactMarkdown>
);

const iconWrap = (background, color) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 22,
  height: 22,
  borderRadius: 6,
  background,
  color,
  flexShrink: 0,
});

const ActionButton = ({ onClick, disabled, primary, children, icon, compact = false, fullWidth = true, fontWeight = 700 }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      width: fullWidth ? '100%' : 'auto',
      minHeight: compact ? 32 : 40,
      padding: compact ? '6px 11px' : '10px 12px',
      borderRadius: radius.pill,
      border: primary ? `1px solid ${colors.primaryStrong}` : `1px solid ${colors.border}`,
      background: primary ? 'linear-gradient(180deg, #60a5fa 0%, #3b82f6 100%)' : colors.surface,
      color: primary ? '#fff' : colors.text,
      boxShadow: primary ? '0 2px 6px rgba(37, 99, 235, 0.22)' : colors.shadow,
      fontSize: compact ? 12 : 14,
      fontWeight,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.7 : 1,
      whiteSpace: 'nowrap',
      flexShrink: 0,
    }}
  >
    {icon}
    <span>{children}</span>
  </button>
);

const actionHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  flexWrap: 'wrap',
};

const actionRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
  gap: 8,
  marginLeft: 'auto',
};

const sectionTitleStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

function AppReportDetailPage() {
  const { reportId } = useParams();
  const {
    report,
    isLoading,
    reportErrorMessage,
    isReportErrorModalOpen,
    setIsReportErrorModalOpen,
    feedbackSubmitting,
    handleFeedback,
    feedbackError,
    hasFeedback,
    effectiveRating,
    effectiveDate,
    audioLoading,
    handleAudioOverviewClick,
    audioStatusLabel,
    audioState,
    audioError,
    audioUrl,
    audioPlayerRef,
    audioSectionRef,
    createdAtLabel,
    navigateToActionPlan,
  } = useReportDetailController({ reportId, isApp: true });

  if (isLoading) {
    return (
      <div style={{ padding: `${spacing.pageY}px 0 24px`, display: 'grid', gap: spacing.sectionGap }}>
        <div
          style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.md,
            padding: spacing.cardPaddingLg,
            textAlign: 'center',
            boxShadow: colors.shadow,
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              margin: '0 auto 12px',
              borderRadius: '50%',
              border: `3px solid ${colors.border}`,
              borderTopColor: colors.primaryStrong,
              animation: 'nhis-spin 0.8s linear infinite',
            }}
          />
          <p style={{ margin: 0, fontSize: 14, color: colors.textMuted }}>리포트를 불러오는 중입니다...</p>
          <style>{`@keyframes nhis-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <MessageModal
        isOpen={isReportErrorModalOpen}
        onClose={() => setIsReportErrorModalOpen(false)}
        title="알림"
        message={reportErrorMessage}
        type="error"
      />
    );
  }

  return (
    <div style={{ padding: `${spacing.pageY}px 0 28px`, maxWidth: 720, margin: '0 auto' }}>
      <div style={{ display: 'grid', gap: spacing.sectionGap }}>
        <div style={actionHeaderStyle}>
          {createdAtLabel && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '4px 10px',
                  borderRadius: radius.pill,
                  background: colors.surfaceMuted,
                  border: `1px solid ${colors.border}`,
                  color: colors.textMuted,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                생성일 {createdAtLabel}
              </span>
            </div>
          )}

          <div style={actionRowStyle}>
            <ActionButton
              onClick={handleAudioOverviewClick}
              disabled={audioLoading}
              compact
              fullWidth={false}
              icon={(
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 19V5" />
                  <path d="M5 12h14" />
                </svg>
              )}
            >
              {audioLoading ? '오디오 준비중...' : '오디오 브리핑'}
            </ActionButton>
            <ActionButton
              onClick={navigateToActionPlan}
              primary
              compact
              fullWidth={false}
              icon={(
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12h14" />
                </svg>
              )}
            >
              실천계획
            </ActionButton>
          </div>
        </div>

        {report.recommendation_overview && (
          <section
            style={{
              background: colors.primarySoft,
              border: `1px solid ${colors.border}`,
              borderRadius: radius.md,
              padding: spacing.cardPadding,
              boxShadow: colors.shadow,
            }}
          >
            <div style={{ ...sectionTitleStyle, marginBottom: 10 }}>
              <span style={iconWrap('#e0e7ff', '#3730a3')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18" />
                  <path d="M7 13l3 3 7-7" />
                </svg>
              </span>
              <h2 style={{ margin: 0, fontSize: 18, lineHeight: 1.3, color: colors.textStrong }}>한눈에 보는 요약</h2>
            </div>
            <p
              style={{
                margin: 0,
                fontSize: 15,
                lineHeight: 1.72,
                color: colors.text,
                whiteSpace: 'pre-line',
                wordBreak: 'normal',
                overflowWrap: 'break-word',
              }}
            >
              {report.recommendation_overview}
            </p>
          </section>
        )}

        {(audioLoading || audioStatusLabel || audioError || audioUrl) && (
          <section
            ref={audioSectionRef}
            style={{
              background: colors.surface,
              border: `1px solid ${audioError ? colors.dangerBorder : colors.border}`,
              borderRadius: radius.md,
              padding: spacing.cardPadding,
              boxShadow: colors.shadow,
            }}
          >
            <div style={sectionTitleStyle}>
              <span style={iconWrap(audioError ? '#fee2e2' : '#eef2ff', audioError ? colors.danger : colors.primaryStrong)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 19V5" />
                  <path d="M5 12h14" />
                </svg>
              </span>
              <h2 style={{ margin: 0, fontSize: 18, lineHeight: 1.3, color: colors.textStrong }}>오디오 브리핑</h2>
            </div>

            {audioStatusLabel && (
              <p style={{ margin: '0 0 8px', color: colors.text, fontSize: 14, lineHeight: 1.6 }}>
                <strong>{audioStatusLabel}</strong>
                {audioState.message ? ` ${audioState.message}` : ''}
              </p>
            )}

            {audioLoading && !audioError && (
              <p style={{ margin: 0, color: colors.textMuted, fontSize: 13, lineHeight: 1.6 }}>
                오디오를 준비 중입니다. {audioState.retryAfter ? `${audioState.retryAfter}초 후 상태를 다시 확인합니다.` : '잠시만 기다려 주세요.'}
              </p>
            )}

            {audioError && (
              <div
                style={{
                  marginTop: 8,
                  padding: '10px 12px',
                  borderRadius: radius.sm,
                  background: colors.dangerSoft,
                  border: `1px solid ${colors.dangerBorder}`,
                  color: colors.danger,
                  fontSize: 13,
                  lineHeight: 1.6,
                  whiteSpace: 'pre-line',
                }}
              >
                {audioError}
              </div>
            )}

            {audioUrl && !audioError && (
              <audio ref={audioPlayerRef} controls src={audioUrl} style={{ width: '100%', marginTop: 12 }}>
                브라우저에서 오디오 태그를 지원하지 않습니다.
              </audio>
            )}
          </section>
        )}

        {report.report_text && (
          <section style={{ marginTop: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, paddingTop: 6 }}>
              <span style={iconWrap('#dcfce7', '#065f46')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16v16H4z" />
                  <path d="M8 8h8M8 12h8M8 16h6" />
                </svg>
              </span>
              <h2 style={{ margin: 0, fontSize: 19, lineHeight: 1.3, color: colors.textStrong }}>AI 상세 분석 결과</h2>
            </div>

            <div
              style={{
                border: `1px solid ${colors.border}`,
                borderRadius: radius.md,
                background: colors.surfaceMuted,
                padding: spacing.cardPadding,
                boxShadow: colors.shadow,
              }}
            >
              <AppMarkdown>{report.report_text}</AppMarkdown>
            </div>
          </section>
        )}

        <div className="feedback-section" style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #eee' }}>
          {!hasFeedback ? (
            <>
              <p style={{ textAlign: 'center', marginBottom: 12 }}>이 분석이 도움이 되셨나요?</p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  className="feedback-button"
                  onClick={() => handleFeedback('helpful')}
                  disabled={feedbackSubmitting}
                  title={undefined}
                >
                  👍 도움이 되었어요
                </button>
                <button
                  className="feedback-button"
                  onClick={() => handleFeedback('needs_improvement')}
                  disabled={feedbackSubmitting}
                  title={undefined}
                >
                  👎 개선이 필요해요
                </button>
              </div>
            </>
          ) : (
            <div className="feedback-message" style={{ width: '100%', textAlign: 'center', marginTop: 8 }}>
              <span className="submitted-badge" style={{ fontSize: 12, color: '#555' }}>
                {effectiveRating === 'helpful'
                  ? '도움이 되셨다니 다행이네요. 감사합니다.'
                  : '개선이 필요하다는 의견 감사합니다. 도움이 될 수 있도록 더욱 노력하겠습니다.'}
                {effectiveDate && (
                  <>
                    <br />
                    <span style={{ fontSize: 11, color: '#777' }}>{new Date(effectiveDate).toLocaleString()}</span>
                  </>
                )}
              </span>
            </div>
          )}

          {feedbackError && (
            <div style={{ width: '100%', textAlign: 'center', marginTop: 8 }}>
              <span className="error-text" style={{ color: '#c00', fontSize: 12 }}>
                {feedbackError}
              </span>
            </div>
          )}
        </div>

        <MessageModal
          isOpen={isReportErrorModalOpen}
          onClose={() => setIsReportErrorModalOpen(false)}
          title="알림"
          message={reportErrorMessage}
          type="error"
        />
      </div>
    </div>
  );
}

export default AppReportDetailPage;
